import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Explicit } from '../../components/ui/Explicit';
import { DrawerLink } from '../../app/DrawerLink';

import { Card } from '../../components/ui/Card';
import { Artwork } from '../dashboard/Artwork';
import { duration, rooms, startsIn, TIER_TONE, type QueueItem, type Room, type RoomView } from './api';
import { Verified } from '../../components/ui/Verified';

type Filter = 'all' | 'boosted' | 'free' | 'first';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'boosted', label: 'Boosted' },
  { id: 'free', label: 'Free' },
  { id: 'first', label: 'New artists' },
];

/**
 * The queue (dev/design/GetVibed DJ Console.dc.html).
 *
 * ## Filtering does not reorder
 *
 * The tabs hide rows; they never renumber them. A submission's position is its
 * place in the *whole* queue, so switching to "New artists" and seeing 04, 07,
 * 11 tells the host where those rows actually sit. A filtered view renumbered
 * 01, 02, 03 would be four lies about when each one plays.
 *
 * ## Reordering is a drag, and it turns auto-weight off
 *
 * The server does that itself when a move arrives - see `QueueItemAction`. The
 * banner says so, because a switch that flips on its own without saying so is
 * indistinguishable from a bug.
 */
export function QueueTable({
  room,
  view,
  onChange,
  onAdd,
}: {
  room: Room;
  view: RoomView;
  onChange: () => void;
  /** Opens the crate — the host's own way into their queue. */
  onAdd: () => void;
}) {
  const [filter, setFilter] = useState<Filter>('all');
  const [dragging, setDragging] = useState<number | null>(null);
  const { queue, weighting } = view;

  const shown = queue.filter((item) =>
    filter === 'all'
      ? true
      : filter === 'boosted'
        ? item.boost_tier === 'basic' || item.boost_tier === 'priority'
        : filter === 'free'
          ? item.boost_tier === 'free'
          : item.first_time_artist,
  );

  /*
   * What `Roll` can land on: what is on screen, minus whatever is already at
   * the top of it. See the button.
   */
  const rollable = shown.slice(1);

  const autoWeight = useMutation({
    mutationFn: (on: boolean) => rooms.settings({ auto_weight: on }),
    onSuccess: onChange,
  });

  const move = useMutation({
    mutationFn: ({ id, after }: { id: number; after: number | null }) => rooms.move(id, after),
    onSuccess: onChange,
  });

  /**
   * One at random, moved to the front.
   *
   * The same request the row's own `next` makes - `move(id, null)` - so there
   * is one way to put something at the top of a queue rather than two that can
   * come to disagree.
   */
  const roll = useMutation({
    mutationFn: (id: number) => rooms.move(id, null),
    onSuccess: onChange,
  });

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/*
          The heading is the tab now, in the console above - two names for one
          list would be one too many, and the count is what this row still owes
          the reader.
        */}
        <span className="font-mono text-[11px] text-faint">
          {queue.length} waiting · {Math.round(view.session.queue_ms / 60000)} min
        </span>

        <div className="flex flex-wrap items-center gap-1.5">
          {/* First, and in the accent, because it is the only control in this
              row that puts something in the queue rather than rearranging what
              arrived. A host with three submissions and an hour to fill needs
              it to be the obvious thing on the screen. */}
          <button
            type="button"
            data-open-crate
            onClick={onAdd}
            className="meta-caps rounded-[var(--radius-pill)] border border-transparent bg-accent px-3 py-2 text-accent-ink transition-opacity hover:opacity-90"
          >
            + Add track
          </button>

          {/*
            A random one, straight to the front.

            ## Why it rolls among what is on screen

            Filtered to Boosted and pressing this should give a boosted track.
            Rolling across the whole queue instead would quietly ignore the
            filter chip lit up beside it, which is the one piece of state a host
            can see while they press this.

            ## Why the current first row is never the answer

            It is already next, so choosing it would leave the queue exactly as
            it was - a button that did nothing, which reads as broken rather
            than as unlucky. Excluding it means every press visibly moves
            something, and it is why two tracks is the minimum for this to be
            offered at all.
          */}
          <button
            type="button"
            title="Play a random one next"
            data-queue-roll
            disabled={rollable.length === 0 || roll.isPending}
            onClick={() => {
              const choice = rollable[Math.floor(Math.random() * rollable.length)];

              if (choice) roll.mutate(choice.id);
            }}
            className="meta-caps rounded-[var(--radius-pill)] border border-line px-3 py-2 text-soft transition-colors hover:border-line-strong hover:text-ink disabled:opacity-30"
          >
            Roll
          </button>

          {FILTERS.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={filter === option.id}
              data-queue-filter={option.id}
              onClick={() => setFilter(option.id)}
              className={`meta-caps rounded-[var(--radius-pill)] border px-3 py-2 transition-colors ${
                filter === option.id
                  ? 'border-transparent bg-accent text-accent-ink'
                  : 'border-line text-soft hover:border-line-strong hover:text-ink'
              }`}
            >
              {option.label}
            </button>
          ))}

          <button
            type="button"
            aria-pressed={room.auto_weight}
            data-auto-weight
            onClick={() => autoWeight.mutate(!room.auto_weight)}
            className={`meta-caps rounded-[var(--radius-pill)] border px-3 py-2 transition-colors ${
              room.auto_weight
                ? 'border-fresh-edge bg-fresh-wash text-fresh'
                : 'border-line text-soft hover:text-ink'
            }`}
          >
            {room.auto_weight ? 'Auto-weight on' : 'Manual order'}
          </button>
        </div>
      </div>

      {/* Said plainly, because the switch flips itself when a row is dragged and
          an unexplained state change reads as a bug. */}
      {!room.auto_weight && (
        <p className="font-mono text-[11px] text-faint">
          Your order. Boosts still arrive and still show their weight, they just
          do not move anything.
        </p>
      )}

      {shown.length === 0 ? (
        <Card className="p-6">
          <p className="text-sm text-soft">
            {queue.length === 0
              ? 'Nothing submitted yet. The queue fills up on its own once people are listening.'
              : 'Nothing matches that filter.'}
          </p>
        </Card>
      ) : (
        <ul className="flex flex-col gap-2" data-queue>
          {shown.map((item) => (
            <li
              key={item.id}
              data-queue-item={item.id}
              draggable
              onDragStart={() => setDragging(item.id)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();

                if (dragging === null || dragging === item.id) return;

                /*
                 * Dropped *onto* a row means "take its place", so the moved row
                 * goes behind whatever is currently in front of it. Sending the
                 * row above as `after` is how the server expresses that, and
                 * null means the very top.
                 */
                const index = queue.findIndex((q) => q.id === item.id);
                const above = index > 0 ? queue[index - 1]!.id : null;

                move.mutate({ id: dragging, after: above === dragging ? item.id : above });
                setDragging(null);
              }}
              onDragEnd={() => setDragging(null)}
              className={dragging === item.id ? 'opacity-40' : ''}
            >
              <QueueRow item={item} onChange={onChange} />
            </li>
          ))}
        </ul>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* The rules, from the server                                        */}
      {/* ---------------------------------------------------------------- */}
      <Card className="flex flex-wrap items-center gap-x-6 gap-y-2 p-4">
        <span className="meta-caps text-faint">Weighting</span>
        {weighting.tiers.map((tier) => (
          <span key={tier.tier} className="flex items-center gap-2 font-mono text-[11px] text-soft">
            <span className={`h-1.5 w-1.5 rounded-full bg-current ${TIER_TONE[tier.tier]}`} aria-hidden />
            {tier.label} · {tier.pinned ? 'pinned' : `×${tier.weight}`}
          </span>
        ))}
        <span className="ml-auto font-mono text-[10px] text-ghost">
          {weighting.bonuses.map((b) => `+${b.weight} ${b.label}`).join(' · ')} · {weighting.tie_break}
        </span>
      </Card>
    </>
  );
}

function QueueRow({ item, onChange }: { item: QueueItem; onChange: () => void }) {
  const tier = useMutation({
    mutationFn: (next: QueueItem['boost_tier']) => rooms.setTier(item.id, next),
    onSuccess: onChange,
  });

  /*
   * "Play next" without pinning.
   *
   * Distinct from `pick`, which is an endorsement the room sees and which
   * survives further submissions arriving above it. This is just an order
   * change - the host wants this one sooner and has no opinion about it beyond
   * that. Moving to `null` is the top, and the server turns auto-weight off
   * because a manual position cannot survive a re-sort.
   */
  const bump = useMutation({
    mutationFn: () => rooms.move(item.id, null),
    onSuccess: onChange,
  });

  const drop = useMutation({
    mutationFn: () => rooms.drop(item.id),
    onSuccess: onChange,
  });

  const next = item.position === 1;

  return (
    <Card
      className={`grid cursor-grab grid-cols-[2.5rem_minmax(0,1fr)_auto] items-center gap-3 p-3 sm:grid-cols-[2.5rem_minmax(0,1fr)_9rem_5rem_auto] ${
        next ? 'border-line-strong' : ''
      }`}
    >
      <div className="flex flex-col">
        <span className={`font-mono text-sm ${next ? 'text-fresh' : 'text-soft'}`}>
          {String(item.position).padStart(2, '0')}
        </span>
        <span className="font-mono text-[9px] text-ghost">{startsIn(item.starts_in_ms)}</span>
      </div>

      <div className="flex min-w-0 items-center gap-3">
        <Artwork
          track={{ title: item.track.title, artwork: null, artwork_small: item.track.artwork_small }}
          className="h-10 w-10 shrink-0 rounded-[var(--radius-chip)]"
        />
        <div className="flex min-w-0 flex-col">
          <DrawerLink
            to={`/tracks/${item.track.id}`}
            className="truncate text-sm font-bold text-ink hover:underline"
          >
            {item.track.explicit && <Explicit className="mr-1.5 align-[-2px]" />}
            {item.track.title}
          </DrawerLink>
          {/*
            Both names open the drawer rather than the profile page.

            A host reads this line to decide something about a submission, and
            the decision is usually "what else have they got" - which is a
            drawer with an add button in it, not a navigation away from a
            console with a queue in flight. See `ArtistDrawer`.
          */}
          <span className="truncate font-mono text-[10px] text-soft">
            <DrawerLink
              to={`/${item.artist.username}`}
              data-queue-artist={item.artist.username}
              className="hover:text-ink hover:underline"
            >
              {item.artist.display_name}
              {item.artist?.verified && <Verified />}
            </DrawerLink>
            {item.submitter ? (
              <>
                {' · '}
                <DrawerLink
                  to={`/${item.submitter.username}`}
                  data-queue-submitter={item.submitter.username}
                  className="hover:text-ink hover:underline"
                >
                  @{item.submitter.username}
                </DrawerLink>
              </>
            ) : item.auto_filled ? (
              /*
                Nobody submitted this - the room topped itself up. Distinct
                from the line below, which means somebody did and has since
                deleted their account; printing that beside every auto-filled
                track would read as a fault rather than a feature.
              */
              ' · auto'
            ) : (
              ' · submitter gone'
            )}
          </span>
        </div>
      </div>

      <div className="hidden flex-col gap-1 sm:flex">
        <span className={`meta-caps ${TIER_TONE[item.boost_tier]}`}>{item.boost_tier}</span>
        {/* The bonuses, not just the total: a host looking at 3.5 beside 3.0
            should see which one made the difference. */}
        <span className="font-mono text-[9px] text-ghost">
          {[item.first_time_artist && 'first time', item.submitter_in_room && 'in room']
            .filter(Boolean)
            .join(' · ') || 'no bonus'}
        </span>
      </div>

      <div className="hidden flex-col gap-1.5 sm:flex">
        <span className="font-mono text-sm text-ink">
          {item.pinned ? 'pinned' : item.weight?.toFixed(1)}
        </span>
        {/* The bar the design draws. Scaled against the priority tier's 8.0
            rather than against the queue's own maximum, so a row does not
            change length because something else was removed. */}
        <div className="h-[3px] overflow-hidden rounded-[var(--radius-pill)] bg-track">
          <div
            className={`h-full rounded-[var(--radius-pill)] bg-current ${TIER_TONE[item.boost_tier]}`}
            style={{ width: `${item.pinned ? 100 : Math.min(100, ((item.weight ?? 0) / 8.8) * 100)}%` }}
          />
        </div>
      </div>

      <div className="flex items-center gap-1.5">
        <button
          type="button"
          title="Move to the top of the queue"
          data-bump={item.id}
          disabled={item.position === 1 || bump.isPending}
          onClick={() => bump.mutate()}
          className="rounded-[var(--radius-chip)] border border-line px-2.5 py-1.5 font-mono text-[10px] text-soft transition-colors hover:border-line-strong hover:text-ink disabled:opacity-30"
        >
          next
        </button>
        <button
          type="button"
          title={item.pinned ? 'Remove host pick' : 'Host pick, which pins above every weight'}
          data-pick={item.id}
          onClick={() => tier.mutate(item.pinned ? 'free' : 'pick')}
          className={`rounded-[var(--radius-chip)] border px-2.5 py-1.5 font-mono text-[10px] transition-colors ${
            item.pinned
              ? 'border-fresh-edge bg-fresh-wash text-fresh'
              : 'border-line text-soft hover:border-line-strong hover:text-ink'
          }`}
        >
          pick
        </button>
        <button
          type="button"
          title="Remove from the queue"
          data-drop={item.id}
          onClick={() => drop.mutate()}
          className="rounded-[var(--radius-chip)] border border-line px-2 py-1.5 text-faint transition-colors hover:border-hot-edge hover:text-hot-text"
        >
          ×
        </button>
      </div>

      {/*
        What the submitter said, which only ever reaches this screen - the API
        omits the field entirely for anybody but the host, so `undefined` means
        "not yours" and an empty string is impossible.

        Spanning the row rather than squeezed into a column: it is a sentence
        written to a person, and truncating it to a cell would make it useless
        at exactly the moment it is meant to be read.
      */}
      {item.note && (
        <p
          data-queue-note={item.id}
          className="col-span-full border-t border-line pt-2.5 text-[13px] leading-relaxed text-soft"
        >
          <span className="meta-caps mr-2 text-ghost">note</span>
          {item.note}
        </p>
      )}

      <span className="sr-only">{duration(item.track.duration_ms)}</span>
    </Card>
  );
}
